import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Play, Pause, Volume2, VolumeX, Maximize, SkipBack, SkipForward, Heart, Share2 } from 'lucide-react';
import { dramaApi, DramaScript } from '../services/api';

const PlayerPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [drama, setDrama] = useState<DramaScript | null>(null);
  const [recommendations, setRecommendations] = useState<DramaScript[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [currentSceneIndex, setCurrentSceneIndex] = useState(0);
  const [choiceFeedback, setChoiceFeedback] = useState<string | null>(null);
  const [isLiked, setIsLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(0);

  const playerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const loadDrama = async () => {
      if (!id) {
        navigate('/explore');
        return;
      }

      try {
        setIsLoading(true);
        const [foundDrama, allDramas] = await Promise.all([
          dramaApi.getDramaById(id),
          dramaApi.getAllDramas(),
        ]);

        if (!foundDrama) {
          navigate('/explore');
          return;
        }

        setDrama(foundDrama);
        setLikeCount(foundDrama.likes);
        setRecommendations(allDramas.filter(item => item.id !== foundDrama.id).slice(0, 3));
        void dramaApi.updateViews(foundDrama.id);
      } finally {
        setIsLoading(false);
      }
    };

    void loadDrama();
  }, [id, navigate]);

  useEffect(() => {
    if (!isPlaying || !drama) {
      return undefined;
    }

    const interval = setInterval(() => {
      setCurrentTime(prev => {
        const nextTime = prev + 1;
        if (nextTime >= drama.duration) {
          setIsPlaying(false);
          return drama.duration;
        }
        return nextTime;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [isPlaying, drama]);

  useEffect(() => {
    if (!drama) {
      return;
    }

    let accumulatedTime = 0;
    let nextSceneIndex = 0;

    for (let index = 0; index < drama.script.scenes.length; index += 1) {
      accumulatedTime += drama.script.scenes[index].duration;
      if (currentTime < accumulatedTime) {
        nextSceneIndex = index;
        break;
      }
      nextSceneIndex = drama.script.scenes.length - 1;
    }

    if (nextSceneIndex !== currentSceneIndex) {
      setCurrentSceneIndex(nextSceneIndex);
    }
  }, [currentTime, currentSceneIndex, drama]);

  const currentScene = drama?.script.scenes[currentSceneIndex];
  const progress = drama ? (currentTime / drama.duration) * 100 : 0;

  const progressStops = useMemo(() => {
    if (!drama) {
      return [];
    }

    let total = 0;
    return drama.script.scenes.map(scene => {
      total += scene.duration;
      return total;
    });
  }, [drama]);

  const handlePlayPause = () => {
    setIsPlaying(prev => !prev);
  };

  const handleSeek = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCurrentTime(parseFloat(event.target.value));
  };

  const handleVolumeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const nextVolume = parseFloat(event.target.value);
    setVolume(nextVolume);
    setIsMuted(nextVolume === 0);
  };

  const toggleMute = () => {
    setIsMuted(prev => !prev);
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement && playerRef.current) {
      void playerRef.current.requestFullscreen();
    } else {
      void document.exitFullscreen();
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatSceneDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return secs > 0 ? `${mins}分${secs}秒` : `${mins}分钟`;
  };

  const handleChoice = (consequence: string, nextScene?: string) => {
    setChoiceFeedback(consequence);
    setIsPlaying(false);

    setTimeout(() => {
      if (!drama) {
        return;
      }

      if (nextScene) {
        const targetIndex = drama.script.scenes.findIndex(scene => scene.id === nextScene);
        if (targetIndex >= 0) {
          setCurrentSceneIndex(targetIndex);
          setCurrentTime(progressStops[targetIndex - 1] || 0);
        }
      } else {
        const nextIndex = Math.min(currentSceneIndex + 1, drama.script.scenes.length - 1);
        setCurrentSceneIndex(nextIndex);
        setCurrentTime(progressStops[nextIndex - 1] || 0);
      }

      setChoiceFeedback(null);
    }, 1800);
  };

  const handleLike = () => {
    if (!drama) {
      return;
    }

    const nextLiked = !isLiked;
    setIsLiked(nextLiked);
    setLikeCount(prev => (nextLiked ? prev + 1 : prev - 1));
    void dramaApi.updateLikes(drama.id, nextLiked);
  };

  const handleShare = async () => {
    if (!drama) {
      return;
    }

    if (navigator.share) {
      await navigator.share({
        title: drama.title,
        text: drama.description,
        url: window.location.href,
      });
      return;
    }

    await navigator.clipboard.writeText(window.location.href);
    alert('链接已复制到剪贴板');
  };

  const goToScene = (index: number) => {
    setCurrentSceneIndex(index);
    setCurrentTime(progressStops[index - 1] || 0);
    setIsPlaying(false);
  };

  if (isLoading || !drama || !currentScene) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-white text-xl">加载中...</div>
      </div>
    );
  }

  const lastDialogue = currentScene.dialogue[currentScene.dialogue.length - 1];

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      <div className="bg-gray-800/90 backdrop-blur-sm p-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center space-x-2 hover:text-purple-400 transition">
            <span className="text-xl">←</span>
            <span>返回首页</span>
          </Link>
          <h1 className="text-xl font-bold">{drama.title}</h1>
          <div className="flex items-center space-x-4">
            <button
              onClick={handleLike}
              className={`flex items-center space-x-1 ${isLiked ? 'text-red-500' : 'hover:text-red-500'} transition`}
            >
              <Heart className={`w-5 h-5 ${isLiked ? 'fill-current' : ''}`} />
              <span>{likeCount}</span>
            </button>
            <button onClick={() => void handleShare()} className="hover:text-purple-400 transition">
              <Share2 className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      <div ref={playerRef} className="relative bg-black">
        <div className="relative aspect-video overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-purple-950 to-gray-950"></div>
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(168,85,247,0.28),transparent_42%),radial-gradient(circle_at_bottom,rgba(34,197,94,0.16),transparent_36%)]"></div>

          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center p-8 bg-black/45 border border-white/10 rounded-lg max-w-4xl mx-4">
              <div className="inline-flex items-center rounded-full bg-white/10 px-3 py-1 text-sm text-purple-200 mb-4">
                当前场景 {currentSceneIndex + 1}/{drama.script.scenes.length}
              </div>
              <h2 className="text-2xl font-bold mb-3">{currentScene.title}</h2>
              <p className="text-gray-300 mb-6">{currentScene.setting}</p>

              <div className="space-y-4 mb-8">
                {currentScene.dialogue.map((dialogue, index) => (
                  <div key={`${dialogue.character}-${index}`} className="text-left">
                    <p className="font-semibold text-purple-300">{dialogue.character}</p>
                    <p className="text-lg">{dialogue.text}</p>
                  </div>
                ))}
              </div>

              {lastDialogue?.choices && (
                <div className="space-y-3">
                  <p className="text-lg mb-4">你会如何回应？</p>
                  {lastDialogue.choices.map((choice) => (
                    <button
                      key={choice.id}
                      onClick={() => handleChoice(choice.consequence, choice.nextScene)}
                      className="block w-full p-4 bg-purple-600 hover:bg-purple-700 rounded-lg transition text-left"
                    >
                      {choice.text}
                    </button>
                  ))}
                </div>
              )}

              {choiceFeedback && (
                <div className="mt-6 p-4 bg-green-600/20 border border-green-400 rounded-lg text-left">
                  <p className="text-green-300">{choiceFeedback}</p>
                </div>
              )}
            </div>
          </div>

          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4">
            <div className="mb-4">
              <input
                type="range"
                min="0"
                max={drama.duration}
                value={currentTime}
                onChange={handleSeek}
                className="w-full h-2 bg-gray-600 rounded-lg appearance-none cursor-pointer"
                style={{ background: `linear-gradient(to right, #9333ea ${progress}%, #4B5563 ${progress}%)` }}
              />
              <div className="flex justify-between text-sm mt-2">
                <span>{formatTime(currentTime)}</span>
                <span>{formatTime(drama.duration)}</span>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <button
                  onClick={() => goToScene(Math.max(0, currentSceneIndex - 1))}
                  className="hover:text-purple-400 transition"
                >
                  <SkipBack className="w-5 h-5" />
                </button>

                <button
                  onClick={handlePlayPause}
                  className="bg-purple-600 hover:bg-purple-700 rounded-full p-3 transition"
                >
                  {isPlaying ? <Pause className="w-6 h-6" /> : <Play className="w-6 h-6" />}
                </button>

                <button
                  onClick={() => goToScene(Math.min(drama.script.scenes.length - 1, currentSceneIndex + 1))}
                  className="hover:text-purple-400 transition"
                >
                  <SkipForward className="w-5 h-5" />
                </button>

                <div className="flex items-center space-x-2">
                  <button onClick={toggleMute} className="hover:text-purple-400 transition">
                    {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
                  </button>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.1"
                    value={isMuted ? 0 : volume}
                    onChange={handleVolumeChange}
                    className="w-20 h-1 bg-gray-600 rounded-lg appearance-none cursor-pointer"
                  />
                </div>
              </div>

              <button onClick={toggleFullscreen} className="hover:text-purple-400 transition">
                <Maximize className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <div className="bg-gray-800 rounded-lg p-6">
              <h2 className="text-xl font-bold mb-4">剧情简介</h2>
              <p className="text-gray-300 mb-4">{drama.description}</p>

              <div className="flex flex-wrap gap-2 mb-4">
                {drama.tags.map((tag) => (
                  <span key={tag} className="px-3 py-1 bg-purple-600/30 text-purple-300 rounded-full text-sm">
                    {tag}
                  </span>
                ))}
              </div>

              <div className="flex flex-wrap items-center gap-4 text-sm text-gray-400">
                <span>类型: {drama.genre}</span>
                <span>时长: {formatSceneDuration(drama.duration)}</span>
                <span>{drama.views.toLocaleString()} 次观看</span>
              </div>
            </div>

            <div className="bg-gray-800 rounded-lg p-6 mt-6">
              <h2 className="text-xl font-bold mb-4">场景选择</h2>
              <div className="space-y-3">
                {drama.script.scenes.map((scene, index) => (
                  <button
                    key={scene.id}
                    onClick={() => goToScene(index)}
                    className={`w-full text-left p-4 rounded-lg transition ${
                      index === currentSceneIndex
                        ? 'bg-purple-600/30 border border-purple-500'
                        : 'bg-gray-700 hover:bg-gray-600'
                    }`}
                  >
                    <div className="flex items-center justify-between gap-4">
                      <div>
                        <h3 className="font-semibold">场景 {index + 1}: {scene.title}</h3>
                        <p className="text-sm text-gray-400">{scene.setting}</p>
                      </div>
                      <span className="text-sm text-gray-400 whitespace-nowrap">{formatSceneDuration(scene.duration)}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <div className="bg-gray-800 rounded-lg p-6">
              <h2 className="text-xl font-bold mb-4">推荐短剧</h2>
              <div className="space-y-4">
                {recommendations.map((recDrama) => (
                  <Link
                    key={recDrama.id}
                    to={`/player/${recDrama.id}`}
                    className="block p-3 bg-gray-700 rounded-lg hover:bg-gray-600 transition"
                  >
                    <h3 className="font-semibold">{recDrama.title}</h3>
                    <p className="text-sm text-gray-400 mt-1">{recDrama.genre}</p>
                    <p className="text-sm text-gray-500 mt-2 line-clamp-2">{recDrama.description}</p>
                  </Link>
                ))}
              </div>
            </div>

            <div className="bg-gray-800 rounded-lg p-6">
              <h2 className="text-xl font-bold mb-4">当前进度</h2>
              <div className="space-y-3 text-sm text-gray-300">
                <div className="flex items-center justify-between">
                  <span>已播放</span>
                  <span>{formatTime(currentTime)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span>当前场景</span>
                  <span>{currentSceneIndex + 1}/{drama.script.scenes.length}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span>互动状态</span>
                  <span>{lastDialogue?.choices ? '可选择' : '剧情推进中'}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlayerPage;
